import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  Container,
  Box,
  Flex,
  List,
  Text,
  Avatar,
  Input,
  Dropdown,
  Button
} from "samba-ui";
import { Link } from "react-router-dom";
import styled from "styled-components";
import _ from "lodash";
import PageLoader from "./PageLoader";
import { loadAdminUsers } from "../redux/users";
import { setPagination } from "../redux/usersPagination";
import SetUserRole from "./SetUserRole";
import { USERS_HEADER_NAVIGATION_ITEMS } from "../constants";
import InviteUserModal from "./InviteUserModal";

const range = (start, stop, step) =>
  Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);

const SubNavigation = styled(Box)`
  padding: 12px 20px;
  border: 1px solid #ececec;
  border-right: none;
  text-align: center;
  cursor: pointer;
  background: ${p => (p.selected ? "#ececec" : "#fff")};
  &:nth-of-type(4) {
    border-right: 1px solid #ececec !important;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  display: block;
  visibility: ${p => (p.disabled ? "hidden" : "visible")};
  pointer-events: ${p => (p.disabled ? "none" : "auto")};
  &:visited {
    color: ${p => p.theme.colors.primary};
  }
`;

const getQuery = selected => {
  return USERS_HEADER_NAVIGATION_ITEMS.filter(n => n.title === selected)[0]
    .query;
};

const ManageUsers = ({
  users,
  loadAdminUsers,
  user,
  pagination,
  setPagination
}) => {
  const isSuperOperator = user.role === "superoperator";
  const [loading, setLoading] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedNavigationItem, setSelectedNavigationItem] = useState(
    "Samba Employees"
  );
  const [selectedSearchOption, setSelectedSearchOption] = useState({
    label: "name",
    value: "name"
  });

  const [isOpen, setIsOpen] = useState(false);

  const debounce = useCallback(
    _.debounce(async () => {
      setLoading(true);
      const query = searchValue
        ? `${selectedSearchOption.value}:${searchValue}*`
        : getQuery(selectedNavigationItem);

      await loadAdminUsers(pagination.selected - 1, query);
      setLoading(false);
    }, 500),
    [
      searchValue,
      selectedSearchOption,
      selectedNavigationItem,
      pagination.selected
    ]
  );

  useEffect(() => {
    debounce();
    return debounce.cancel;
  }, [debounce]);

  const onClickNavigation = selected => {
    setSelectedNavigationItem(selected);
    setPagination(1);
  };

  const paginationOptions = range(1, pagination.end, 1).map(p => ({
    label: p,
    value: p
  }));

  const searchOptions = [
    { label: "name", value: "name" },
    { label: "family name", value: "family_name" },
    { label: "email", value: "email" },
    { label: "email domain", value: "email.domain" }
  ];

  const onInviteUserClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Container maxWidth={2048} minWidth={960}>
      <Flex>
        <Box width={3 / 4} pr={4}>
          <Flex width={"100%"} mb={3}>
            {USERS_HEADER_NAVIGATION_ITEMS.map(h => {
              return (
                <SubNavigation
                  selected={h.title === selectedNavigationItem}
                  key={h.title}
                  onClick={() => onClickNavigation(h.title)}
                >
                  <Text fontSize={2} fontWeight={500}>
                    {h.title}
                  </Text>
                </SubNavigation>
              );
            })}
            <Flex style={{ marginLeft: "auto" }} alignItems="center">
              <Text>Page</Text>
              <Box width={75} px={2}>
                <Dropdown
                  options={paginationOptions}
                  selected={{
                    label: pagination.selected,
                    value: pagination.selected
                  }}
                  onSelect={selected => setPagination(selected.value)}
                />
              </Box>
              of {pagination.end}
            </Flex>
          </Flex>
          {loading ? (
            <PageLoader />
          ) : (
            <Box>
              <List.Header borderColor="#d6d6d6 !important">
                <Flex justifyContent="space-between" alignItems="center">
                  <Box>
                    <Text fontSize={1} color="secondary" fontWeight={500}>
                      Users
                    </Text>
                  </Box>
                </Flex>
              </List.Header>
              {Object.keys(users).length ? (
                Object.keys(users).map(userId => {
                  const { imageURL, name, email, role } = users[userId];
                  return (
                    <List.Item
                      borderColor="#d6d6d6 !important"
                      key={email}
                      disabled={!role}
                    >
                      <Flex alignItems="center" justifyContent="space-between">
                        <Flex width={3 / 6}>
                          <Avatar
                            src={imageURL}
                            mr={2}
                            width={40}
                            height={40}
                          />
                          <Box>
                            <Box>
                              <Text fontSize={1} color="palette.ink.normal">
                                {name}
                              </Text>
                            </Box>
                            <Text fontSize={1} color="palette.ink.normal">
                              {email}
                            </Text>
                          </Box>
                        </Flex>
                        <Box width={200}>
                          <SetUserRole user={users[userId]} profile={user} />
                        </Box>
                        <StyledLink
                          to={`/admin/users/${userId}`}
                          disabled={isSuperOperator || !role}
                          variant="primaryBordered"
                        >
                          Manage
                        </StyledLink>
                      </Flex>
                    </List.Item>
                  );
                })
              ) : (
                <List.Empty>No users found</List.Empty>
              )}
            </Box>
          )}
        </Box>
        <Box width={1 / 4} pt={5}>
          <Flex mb={2} alignItems="center">
            <Text fontWeight={1} fontSize={0} color="palette.ink.light">
              Search users by
            </Text>
            <Box width={190} ml={2}>
              <Dropdown
                options={searchOptions}
                selected={selectedSearchOption}
                onSelect={setSelectedSearchOption}
              />
            </Box>
          </Flex>
          <Input
            style={{ width: "100%" }}
            placeholder="Search..."
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
          />
          {user.role === "superadmin" && (
            <Box mt={3}>
              <Button style={{ width: "100%" }} onClick={onInviteUserClick}>
                Invite User
              </Button>
            </Box>
          )}
        </Box>
      </Flex>
      <InviteUserModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </Container>
  );
};

const mapStateToProps = state => ({
  users: state.users,
  pagination: state.usersPagination
});

const mapDispatchToProps = dispatch => ({
  loadAdminUsers: (page, query) => dispatch(loadAdminUsers(page, query)),
  setPagination: start => dispatch(setPagination(start))
});

ManageUsers.propTypes = {
  users: PropTypes.object,
  loadAdminUsers: PropTypes.func,
  setPagination: PropTypes.func,
  user: PropTypes.object,
  pagination: PropTypes.object
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageUsers);
